import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF, Bounds } from '@react-three/drei';
import { apiUrl } from '../lib/api';
import type { Model } from '../types';

interface ModelCardProps {
  model: Model;
}

function PreviewModel({ url }: { url: string }) {
  const { scene } = useGLTF(url);
  return <primitive object={scene} />;
}

/**
 * Live 3D preview used as a card thumbnail. Persisted thumbnail images are not
 * generated yet (see README limitations), so we render the GLB directly. Kept
 * lightweight: no Stage/environment, capped DPR, auto-rotate only.
 */
function CardPreview({ url }: { url: string }) {
  return (
    <Canvas dpr={[1, 1.5]} camera={{ position: [0, 0.6, 3], fov: 40 }}>
      <ambientLight intensity={0.8} />
      <directionalLight position={[3, 5, 2]} intensity={1.3} />
      <directionalLight position={[-3, 2, -2]} intensity={0.4} />
      <Suspense fallback={null}>
        <Bounds fit clip observe margin={1.1}>
          <PreviewModel url={url} />
        </Bounds>
      </Suspense>
      <OrbitControls autoRotate autoRotateSpeed={2} enableZoom={false} enablePan={false} />
    </Canvas>
  );
}

export default function ModelCard({ model }: ModelCardProps) {
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div
        style={{
          aspectRatio: '4/3',
          background: '#0f172a',
          borderRadius: '0.5rem',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {model.thumbnailUrl ? (
          <img
            src={model.thumbnailUrl}
            alt={model.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : model.modelUrl ? (
          <CardPreview url={apiUrl(model.modelUrl)} />
        ) : (
          <span style={{ fontSize: '2.5rem' }}>🧊</span>
        )}
      </div>
      <h3 style={{ margin: 0, fontSize: '1rem' }}>
        <Link to={`/models/${model.id}`}>{model.name}</Link>
      </h3>
      <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.85rem' }}>
        {model.description || 'No description'}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
        <span style={{ color: model.isPublic ? 'var(--success)' : 'var(--muted)' }}>
          {model.isPublic ? 'Public' : 'Private'}
        </span>
      </div>
    </div>
  );
}
